import { type AnalyticsPlugin } from "analytics";
import type { PostHogConfig } from "posthog-js";
import { PostHog } from "posthog-node";

export interface PostHogPluginConfig {
  token?: string;
  enabled?: boolean;
  options?: Partial<PostHogConfig>;
}

const name = "posthog";

type Params = any;

export default function posthogPlugin(config: PostHogPluginConfig): AnalyticsPlugin {
  let client: PostHog | null = null;

  const debug = (message: string, data?: any) => {
    if (config.options?.debug) {
      console.debug(`[PostHog Debug] ${message}`, data || "");
    }
  };

  return {
    name,
    config,
    initialize: () => {
      if (!config.token) {
        throw new Error("PostHog token is required");
      }

      client = new PostHog(config.token, { host: "https://us.i.posthog.com" });
      debug("PostHog initialized", { host: "https://us.i.posthog.com" });
    },
    // track event
    track: ({ payload }: Params) => {
      try {
        if (!client) {
          debug("Track failed: Client not initialized");
          return;
        }

        debug("Tracking event", {
          event: payload.event,
          distinctId: payload.userId ?? payload?.properties?.user_id,
          properties: payload.properties,
        });

        client.capture({
          distinctId: payload.userId ?? payload?.properties?.user_id,
          event: payload.event,
          properties: payload.properties,
        });
      } catch (error) {
        debug("Track error", error);
        throw error;
      }
    },
    // identify user
    identify: ({ payload }: Params) => {
      try {
        if (!client) {
          debug("Identify failed: Client not initialized");
          return;
        }

        const { userId, traits } = payload;
        const set = traits.$set ?? traits;
        const setOnce = traits.$set_once ?? {};

        debug("Identifying user", {
          distinctId: userId,
          properties: { ...set, $set_once: setOnce },
        });

        client.identify({
          distinctId: userId,
          properties: {
            ...set,
            $set_once: setOnce,
          },
        });
      } catch (error) {
        debug("Identify error", error);
        throw error;
      }
    },
    loaded: () => {
      return Boolean(client);
    },
    reset: () => {
      client = null;
    },
  };
}
